import React, { type FC, useCallback, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { RepositoryChatConversation } from "../../dashboard-client";
import type { SourceControlRepository } from "../../types";

import { useAllRepositories } from "../../hooks/useAllRepositories";
import { useRepositoryChatEnabled } from "../../hooks/useRepositoryChatEnabled";
import { useRepositoryChatWorkspace } from "../../hooks/useRepositoryChatWorkspace";
import { updateSearchParams } from "../../utils/searchParams";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { LoadingState } from "../common/LoadingState";
import { RepositoryChat } from "./RepositoryChat";
import { type RepositoryChatRetrievalMode } from "./repositoryChatModels";
import { RepositoryChatSidebar } from "./RepositoryChatSidebar";

const CONVERSATION_ID_PARAM = "conversationId";

export const RepositoryChatPage: FC = () => {
  const repositoryChat = useRepositoryChatEnabled();

  if (repositoryChat.status === "loading") {
    return <LoadingState message="Checking repository chat access..." />;
  }

  if (!repositoryChat.enabled) {
    return (
      <Alert variant="warning" title="Repository chat unavailable">
        Repository chat is disabled for this organization.
      </Alert>
    );
  }

  return <RepositoryChatPageContent />;
};

const RepositoryChatPageContent: FC = () => {
  const { repositories, loading: repositoriesLoading } = useAllRepositories();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedConversationId = sanitizeConversationIdParam(
    searchParams.get(CONVERSATION_ID_PARAM),
  );
  const selectConversation = useCallback(
    (conversationId: null | string) => {
      setSearchParams(
        (current) =>
          updateSearchParams(current, {
            [CONVERSATION_ID_PARAM]: conversationId,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const {
    conversations,
    creatingConversation,
    error,
    loadingConversation,
    loadingConversations,
    refreshSelectedConversation,
    selectedConversation,
    startConversation,
  } = useRepositoryChatWorkspace(selectedConversationId, selectConversation);
  const [selectedRepositorySlugs, setSelectedRepositorySlugs] = useState<
    string[]
  >([]);
  const [repoSearch, setRepoSearch] = useState("");
  const [retrievalMode, setRetrievalMode] =
    useState<RepositoryChatRetrievalMode>("hybrid");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  async function handleCreateConversation() {
    const selectedRepositories = repositories.filter((repository) =>
      selectedRepositorySlugs.includes(repository.remote_slug),
    );
    const [primaryRepository] = selectedRepositories;
    if (!primaryRepository) {
      return;
    }

    const conversation = await startConversation({
      autoExpandEnabled: false,
      primaryRepositorySlug: primaryRepository.remote_slug,
      repositoryScope: selectedRepositories.map(
        (repository) => repository.remote_slug,
      ),
      sourceControlProviderId: primaryRepository.provider_id,
    });

    if (!conversation) {
      return;
    }

    setSelectedRepositorySlugs(conversation.repositoryScope);
  }

  function handleToggleRepository(repository: SourceControlRepository) {
    setSelectedRepositorySlugs((current) =>
      current.includes(repository.remote_slug)
        ? current.filter((slug) => slug !== repository.remote_slug)
        : [...current, repository.remote_slug],
    );
  }

  if (!repositoriesLoading && repositories.length === 0) {
    return (
      <EmptyState
        showRobot
        title="No repositories connected"
        description="Connect GitHub and add repositories before opening a repo chat workspace."
        action={
          <Link to="/integrations?tab=source-control">
            <Button type="button">Open Integrations</Button>
          </Link>
        }
      />
    );
  }

  const mainContent = renderMainContent(
    loadingConversation,
    refreshSelectedConversation,
    retrievalMode,
    selectedConversation,
  );

  function handleSelectConversationMobile(conversationId: string) {
    selectConversation(conversationId);
    setSidebarOpen(false);
  }

  return (
    <div className="flex min-h-[30rem] flex-1 flex-col overflow-hidden border border-slate-200 dark:border-slate-700">
      <RepositoryChatDegradedNotice />
      <div className="relative flex min-h-0 flex-1 gap-0 overflow-hidden">
        {sidebarOpen ? (
          <button
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            type="button"
            aria-label="Close sidebar"
          />
        ) : null}

        <div
          className={`fixed inset-y-0 left-0 z-50 w-80 transition-transform duration-200 ease-in-out lg:relative lg:inset-auto lg:z-auto lg:flex-shrink-0 lg:transition-[width] ${
            sidebarOpen
              ? "translate-x-0 lg:w-80"
              : "-translate-x-full lg:w-0 lg:translate-x-0"
          } lg:overflow-hidden`}
        >
          <RepositoryChatSidebar
            conversations={conversations}
            creatingConversation={creatingConversation}
            loadingConversations={loadingConversations}
            loadingRepositories={repositoriesLoading}
            onCreateConversation={() => void handleCreateConversation()}
            onRepoSearchChange={setRepoSearch}
            onRetrievalModeChange={setRetrievalMode}
            onSelectConversation={handleSelectConversationMobile}
            onToggleRepository={handleToggleRepository}
            repoSearch={repoSearch}
            repositories={repositories}
            retrievalMode={retrievalMode}
            selectedConversationId={selectedConversationId}
            selectedRepositorySlugs={selectedRepositorySlugs}
          />
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {error ? (
            <Alert variant="warning" className="mx-4 mt-2">
              {error}
            </Alert>
          ) : null}

          {mainContent}
        </div>

        <SidebarToggle open={sidebarOpen} onToggle={setSidebarOpen} />
      </div>
    </div>
  );
};

export const RepositoryChatDegradedNotice: FC = () => (
  <Alert variant="info" className="rounded-none border-x-0 border-t-0">
    Repository chat is in beta. Retrieval-backed answers are enabled, but
    citations may not display correctly after page reload yet.
  </Alert>
);

const SidebarToggle: FC<{
  onToggle: (open: boolean) => void;
  open: boolean;
}> = ({ onToggle, open }) => (
  <button
    className="absolute left-1.5 top-1.5 z-30 flex h-8 w-8 items-center justify-center border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-700 active:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
    onClick={() => onToggle(!open)}
    title={open ? "Collapse sidebar" : "Expand sidebar"}
    type="button"
  >
    {open ? (
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path
          d="M11 19l-7-7 7-7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18 19l-7-7 7-7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ) : (
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path
          d="M4 6h16M4 12h16M4 18h16"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )}
  </button>
);

function renderMainContent(
  loadingConversation: boolean,
  refreshSelectedConversation: () => Promise<void>,
  retrievalMode: RepositoryChatRetrievalMode,
  selectedConversation: RepositoryChatConversation | null,
) {
  if (loadingConversation && !selectedConversation) {
    return (
      <div className="flex flex-1 items-center justify-center bg-white dark:bg-arti-panel">
        <LoadingState className="max-w-3xl" rows={6} rowHeight="h-16" />
      </div>
    );
  }

  if (selectedConversation) {
    return (
      <RepositoryChat
        conversation={selectedConversation}
        onConversationChanged={refreshSelectedConversation}
        retrievalMode={retrievalMode}
      />
    );
  }

  return (
    <EmptyState
      showRobot
      title="Start with a repository set"
      description="Choose one or more repositories in the left rail, then start a chat to get a dedicated reasoning workspace."
      className="flex-1"
    />
  );
}

function sanitizeConversationIdParam(value: null | string): null | string {
  const normalized = value?.trim();
  return normalized || null;
}
